import React from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  useMediaQuery,
  alpha,
  styled
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useSection, SECTIONS, SectionType } from '../../contexts/SectionContext';

// Custom styled tab
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9rem',
  minHeight: 44,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderRadius: '8px 8px 0 0',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
    minHeight: 40,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    minWidth: 'auto',
  },
}));

interface SectionTabsProps {
  elevated?: boolean;
}

const SectionTabs: React.FC<SectionTabsProps> = ({ elevated = false }) => {
  const { currentSection, setSection, sections } = useSection();
  const { currentTheme } = useThemeContext();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(currentTheme.breakpoints.down('sm'));

  const handleChange = (_event: React.SyntheticEvent, newValue: SectionType) => {
    setSection(newValue);
  };

  // Handle direct click — navigate to section home even if already selected
  const handleTabClick = (sectionId: SectionType) => {
    if (sectionId === currentSection) {
      // Already on this section but possibly on a sub-page — navigate to home
      navigate(SECTIONS[sectionId].basePath);
    }
  };

  const sectionList = Object.values(sections) as typeof SECTIONS[keyof typeof SECTIONS][];

  return (
    <Box
      sx={{
        backgroundColor: (theme) => alpha(theme.palette.background.default, 0.98),
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: elevated ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
      }}
    >
      <Box
        sx={{
          maxWidth: 'lg',
          margin: '0 auto',
          px: { xs: 0.5, sm: 2, md: 3 },
        }}
      >
        <Tabs
          value={currentSection}
          onChange={handleChange}
          aria-label="Main section tabs"
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            minHeight: { xs: 40, sm: 44 },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          {sectionList.map((section) => (
            <StyledTab
              key={section.id}
              value={section.id}
              onClick={() => handleTabClick(section.id)}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                  <Typography component="span" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                    {section.icon}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: currentSection === section.id ? 700 : 600,
                        fontSize: { xs: '0.78rem', sm: '0.9rem' },
                        lineHeight: 1.2,
                      }}
                    >
                      {section.label}
                    </Typography>
                    {!isMobile && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          lineHeight: 1.2,
                          opacity: currentSection === section.id ? 1 : 0.7,
                        }}
                      >
                        {section.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
              aria-label={`${section.label} - ${section.description}`}
            />
          ))}
        </Tabs>
      </Box>
    </Box>
  );
};

export default SectionTabs;
